import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const CouponSchema = Yup.object().shape({
	code: Yup.string()
		.min(3, "Código muito curto")
		.max(30, "Código muito longo")
		.required("Informe o código"),
	amount: Yup.number().min(0, "Valor inválido"),
	durationMonths: Yup.number().min(0, "Valor inválido"),
	maxUses: Yup.number().min(0, "Valor inválido"),
});

// Cada tipo muda o que o campo "valor" significa — por isso o rótulo é dinâmico.
const TIPOS = [
	{
		value: "percent",
		rotulo: "Porcentagem",
		campo: "Desconto (%)",
		ajuda: "Tira uma porcentagem da mensalidade.",
	},
	{
		value: "fixed",
		rotulo: "Valor fixo",
		campo: "Desconto (R$)",
		ajuda: "Tira um valor em reais da mensalidade.",
	},
	{
		value: "months",
		rotulo: "Meses grátis",
		campo: "Quantos meses",
		ajuda: "A empresa não paga nada durante esse período.",
	},
];

const CouponModal = ({ open, onClose, couponId }) => {
	const initialState = {
		code: "",
		description: "",
		type: "percent",
		amount: 0,
		durationMonths: 0,
		validUntil: "",
		maxUses: 0,
		isActive: true,
	};

	const [coupon, setCoupon] = useState(initialState);

	useEffect(() => {
		if (!open) {
			setCoupon(initialState);
			return;
		}

		(async () => {
			if (!couponId) return;
			try {
				const { data } = await api.get("/coupons");
				const atual = data.find((c) => c.id === couponId);

				if (atual) {
					setCoupon({
						...initialState,
						...atual,
						amount: Number(atual.amount || 0),
						// O input date só aceita "aaaa-mm-dd".
						validUntil: atual.validUntil
							? String(atual.validUntil).slice(0, 10)
							: "",
					});
				}
			} catch (err) {
				toastError(err);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [couponId, open]);

	const salvar = async (valores) => {
		const corpo = {
			...valores,
			amount: Number(valores.amount) || 0,
			durationMonths: Number(valores.durationMonths) || 0,
			maxUses: Number(valores.maxUses) || 0,
			validUntil: valores.validUntil || null,
		};

		try {
			if (couponId) {
				await api.put(`/coupons/${couponId}`, corpo);
			} else {
				await api.post("/coupons", corpo);
			}
			toast.success(couponId ? "Cupom atualizado." : "Cupom criado.");
			onClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{couponId ? "Editar cupom" : "Novo cupom"}</DialogTitle>
				</DialogHeader>

				<Formik
					initialValues={coupon}
					enableReinitialize
					validationSchema={CouponSchema}
					onSubmit={async (valores, actions) => {
						await salvar(valores);
						actions.setSubmitting(false);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => {
						const tipo = TIPOS.find((t) => t.value === values.type) || TIPOS[0];

						return (
							<Form className="space-y-4">
								<div className="space-y-1.5">
									<Label htmlFor="code">Código</Label>
									<Field
										as={Input}
										id="code"
										name="code"
										placeholder="BEMVINDO20"
										className="uppercase"
										onChange={(e) =>
											setFieldValue("code", e.target.value.toUpperCase())
										}
									/>
									<p className="text-xs text-muted-foreground">
										É o que o cliente digita para ganhar o desconto.
									</p>
									{touched.code && errors.code && (
										<p className="text-xs text-destructive">{errors.code}</p>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="description">Descrição</Label>
									<Field
										as={Input}
										id="description"
										name="description"
										placeholder="20% nos 3 primeiros meses"
									/>
								</div>

								<div className="space-y-1.5">
									<Label>Tipo de desconto</Label>
									<div className="grid grid-cols-3 gap-2">
										{TIPOS.map((t) => (
											<button
												key={t.value}
												type="button"
												aria-pressed={values.type === t.value}
												onClick={() => setFieldValue("type", t.value)}
												className={
													values.type === t.value
														? "rounded-lg border border-primary bg-primary/10 px-2 py-2 text-xs font-medium text-primary"
														: "rounded-lg border px-2 py-2 text-xs text-muted-foreground hover:bg-muted"
												}
											>
												{t.rotulo}
											</button>
										))}
									</div>
									<p className="text-xs text-muted-foreground">{tipo.ajuda}</p>
								</div>

								<div className="grid grid-cols-2 gap-3">
									<div className="space-y-1.5">
										<Label htmlFor="amount">{tipo.campo}</Label>
										<Field as={Input} id="amount" name="amount" type="number" />
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="durationMonths">Duração (meses)</Label>
										<Field
											as={Input}
											id="durationMonths"
											name="durationMonths"
											type="number"
										/>
										<p className="text-xs text-muted-foreground">
											0 = para sempre
										</p>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="maxUses">Limite de usos</Label>
										<Field
											as={Input}
											id="maxUses"
											name="maxUses"
											type="number"
										/>
										<p className="text-xs text-muted-foreground">
											0 = sem limite
										</p>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="validUntil">Válido até</Label>
										<Field
											as={Input}
											id="validUntil"
											name="validUntil"
											type="date"
										/>
										<p className="text-xs text-muted-foreground">
											Em branco = sem prazo
										</p>
									</div>
								</div>

								<div className="flex items-center justify-between rounded-lg border p-3">
									<div>
										<Label htmlFor="isActive">Cupom ativo</Label>
										<p className="text-xs text-muted-foreground">
											Desativado, ninguém consegue aplicar.
										</p>
									</div>
									<Switch
										id="isActive"
										checked={values.isActive}
										onCheckedChange={(v) => setFieldValue("isActive", v)}
									/>
								</div>

								<DialogFooter>
									<Button type="button" variant="outline" onClick={onClose}>
										Cancelar
									</Button>
									<Button type="submit" disabled={isSubmitting}>
										{isSubmitting && (
											<Loader2 className="h-4 w-4 animate-spin" />
										)}
										{couponId ? "Salvar" : "Criar cupom"}
									</Button>
								</DialogFooter>
							</Form>
						);
					}}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default CouponModal;
